import { PRICING_RULES } from './pricing-rules-data.ts'

export { PRICING_RULES } from './pricing-rules-data.ts'

/**
 * THE RULES THAT MOVE MONEY, IN ONE REGISTRY.
 *
 * Every percentage, every flat amount and every multiplier that changes what
 * somebody is charged or paid used to be a `const` in the file that happened to
 * apply it — fourteen of them, across nine files, each editable only by a
 * deploy. Spec §12 already said one of them was the Admin's to set („stawka
 * edytowalna przez Admina"), and that one grew a `site_setting` key while the
 * other thirteen did not.
 *
 * This is that key, generalised. One row per rule in `site_setting`, one entry
 * here describing it, and the Cennik screen draws its boxes off this list
 * rather than off a hand-written form — so a rule added here appears in the
 * panel with no screen to edit.
 *
 * ── WHY IN `@repo/db` ────────────────────────────────────────────────────
 * The same reason `travel.ts`, `shoot-price.ts` and `shoot-plans.ts` are here:
 * the checkout prices a line INSIDE a database transaction and cannot reach for
 * `@repo/core`, and the panel's editor is a client component that must not pull
 * the database into the browser bundle. Both can import this, because it is a
 * list and a parser and nothing else.
 *
 * ── WHY ONE KEY PER RULE, NOT ONE BLOB ───────────────────────────────────
 * `production.travel_rate_net_minor_per_km` has been a key of its own since
 * spec §12 and is already seeded. Folding it into a `pricing.rules` object
 * would leave two places holding the travel rate for as long as one deployment
 * lagged the other — which is the exact fault this registry exists to end. So
 * every rule keeps its own key, {@link readPricingRuleKeys} fetches them in one
 * `WHERE key IN (…)`, and the travel rate slots in with no migration at all.
 *
 * Defaults are the values the code charged before this file existed. Changing
 * one of THOSE is a business decision; moving a rule in here is not meant to be.
 */

/** How a value reads, which is also how the panel renders and validates it. */
export type PricingRuleUnit =
  /** Net grosze. Shown and typed in złoty. */
  | 'MINOR'
  /** Basis points — 10 000 is 100 %. Shown and typed in percent. */
  | 'BPS'
  /** A bare multiplier: 2 means "twice the daily rate". */
  | 'FACTOR'
  /** Whole days. */
  | 'DAYS'
  /** Whole hours — a lead time or a window. */
  | 'HOURS'
  /** A bare count: rounds, pieces, how many times something may happen. */
  | 'COUNT'

/** Which drawer section a rule is typed in. */
export type PricingRuleGroup = 'zwroty' | 'zlecenie' | 'koszyk' | 'sprzet' | 'ustawowe'

export interface PricingRule {
  /** The `site_setting.key` this rule is stored under. Stable; never renamed. */
  readonly key: string
  readonly group: PricingRuleGroup
  /** What the box is called, in the owner's words. */
  readonly label: string
  /** One sentence under it: what moves when this number moves. */
  readonly hint: string
  readonly unit: PricingRuleUnit
  /** What applied before the rule was editable, and what applies when unset. */
  readonly defaultValue: number
  /** Inclusive bounds. Refused on save rather than clamped — see the service. */
  readonly min: number
  readonly max: number
  /**
   * `false` for the two values FLOU does not get to choose.
   *
   * They are ON the screen because somebody retuning a refund needs to see the
   * VAT it will be grossed at, and because a statutory figure nobody can find
   * is a figure somebody re-types into a contract from memory. They have no
   * input: a 7-day withdrawal window is void by law, and a VAT rate typed here
   * would disagree with every invoice already issued at 23 %.
   */
  readonly editable: boolean
  /** Why it cannot be typed. Rendered instead of the input. */
  readonly lockedBecause?: string
}

/** Values keyed by rule key — what a reader hands to the arithmetic. */
export type PricingRuleValues = Readonly<Record<string, number>>

/**
 * Read one rule out of a value map, falling back to its default.
 *
 * Total on purpose: a caller asking for a key the registry does not carry gets
 * `0` rather than `undefined` leaking into a multiplication. The registry is
 * the only thing that mints keys, so that branch is unreachable in practice and
 * is here to keep the return type a number.
 */
export function ruleValue(values: PricingRuleValues, key: string): number {
  const stored = values[key]
  if (typeof stored === 'number' && Number.isFinite(stored)) return stored
  return pricingRule(key)?.defaultValue ?? 0
}

const BY_KEY = new Map(PRICING_RULES.map((rule) => [rule.key, rule]))

export function pricingRule(key: string): PricingRule | undefined {
  return BY_KEY.get(key)
}

/** Every key the registry knows, for the bulk read. */
export const PRICING_RULE_KEYS: readonly string[] = PRICING_RULES.map((rule) => rule.key)

/** What applies when nobody has ever saved anything — today's charged values. */
export const DEFAULT_PRICING_RULES: PricingRuleValues = Object.freeze(
  Object.fromEntries(PRICING_RULES.map((rule) => [rule.key, rule.defaultValue])),
)

/**
 * Turn stored `site_setting` rows into a value map.
 *
 * A row that is missing, is not a number, or falls outside the rule's own
 * bounds falls back to the default — a stored value the registry would refuse
 * to accept today is a value it must not APPLY today either, whatever put it
 * there. Silent rather than throwing: the alternative is a checkout that 500s
 * because somebody once saved a bad number.
 */
export function parsePricingRules(rows: ReadonlyMap<string, unknown>): PricingRuleValues {
  const out: Record<string, number> = {}
  for (const rule of PRICING_RULES) {
    const raw = rows.get(rule.key)
    out[rule.key] =
      typeof raw === 'number' && Number.isFinite(raw) && raw >= rule.min && raw <= rule.max
        ? raw
        : rule.defaultValue
  }
  return Object.freeze(out)
}

/** Why this value may not be saved against this rule — or `null` when it may. */
export function pricingRuleProblem(key: string, value: number): string | null {
  const rule = pricingRule(key)
  if (!rule) return 'Nie znamy takiej reguły.'
  if (!rule.editable) return rule.lockedBecause ?? 'Tej wartości nie ustala FLOU.'
  if (!Number.isFinite(value)) return 'To musi być liczba.'
  if (!Number.isInteger(value)) return 'Podaj liczbę całkowitą.'
  if (value < rule.min || value > rule.max) return boundsMessage(rule)
  return null
}

function boundsMessage(rule: PricingRule): string {
  const fmt = (value: number) =>
    rule.unit === 'MINOR'
      ? `${String(value / 100)} zł`
      : rule.unit === 'BPS'
        ? `${String(value / 100)}%`
        : String(value)
  return `Wartość musi mieścić się między ${fmt(rule.min)} a ${fmt(rule.max)}.`
}

/**
 * ── WHAT GETS FROZEN, AND WHY ONLY THESE ─────────────────────────────────
 *
 * Most rules here are spent the moment they are read: a coordinator fee, a
 * footage surcharge, a code's discount and a travel leg all turn into an
 * AMOUNT at checkout, and that amount is written onto the line. Retuning the
 * rule tomorrow cannot reach them, because there is no percentage left in the
 * row — only złoty.
 *
 * These are the exceptions. They are read long after the customer agreed to
 * them: a refund is worked out when somebody cancels, a late fee when the
 * camera comes back, a revision ceiling when the third round is asked for. Left
 * live, a number typed in Cennik today would change what a reservation made
 * last month is worth — which is not a price change, it is a change to a deal
 * that was already struck. So the row carries its own copy.
 */
export const RULES_FROZEN_ON_BOOKING: readonly string[] = [
  'refund.full_bps',
  'refund.half_bps',
  'refund.none_bps',
  'refund.performer_share_bps',
  'rental.late_fee_multiplier',
]

export const RULES_FROZEN_ON_ORDER: readonly string[] = ['revision.extra_max_bps']

/** The subset to stamp onto a row being created. */
export function freezeRules(values: PricingRuleValues, keys: readonly string[]): PricingRuleValues {
  return Object.freeze(Object.fromEntries(keys.map((key) => [key, ruleValue(values, key)])))
}

/**
 * The rules in force for one row: whatever it froze, over whatever is live now.
 *
 * A NULL or unreadable snapshot falls through to `live` for every key, which is
 * exactly right for the rows that existed before the column did — they were
 * always judged by the rule of the day, and nothing about this change should
 * retroactively decide they were not.
 */
export function thawRules(snapshot: unknown, live: PricingRuleValues): PricingRuleValues {
  if (!snapshot || typeof snapshot !== 'object') return live
  const frozen = snapshot as Record<string, unknown>
  const out: Record<string, number> = { ...live }
  for (const [key, raw] of Object.entries(frozen)) {
    if (typeof raw === 'number' && Number.isFinite(raw)) out[key] = raw
  }
  return Object.freeze(out)
}
